import { Alert, Box, FormHelperText, Skeleton, Typography } from '@mui/material'
import CardActions from 'src/pages/sistema/components/CardActions'
import { useEffect, useMemo } from 'react'
import { UseFormReturn, useWatch } from 'react-hook-form'
import { AsyncSelect } from 'src/components/forms'
import { ITableColumn, ITableGroups, TableList } from 'src/components/table'
import { useLoadOptions } from 'src/hooks/useLoadOptions'
import { EntityWithIdAndDescriptionFields } from 'src/types'
import { OperacionAFOCLFields, OperacionAFOCLTablaValorFields } from '../types/fields'
import { FormOperacionAFOCL } from '../types/types'
import { OperacionAFOCLTablaValorVistaInicial } from '../types/dto'
import { useAFOCLTablaValorInicial, AFOCLRow } from '../hooks/useTablaValorAFOCL'
import { useAFOPUAforoTipos } from '../../afopu/hooks/useAFOPUCache'
import UseBgColor from 'src/@core/hooks/useBgColor'

interface SeccionNuevoAFOCLTablaValoresProps {
  formMethods: UseFormReturn<FormOperacionAFOCL>
  codSistemaPrincipal: number
}

const SeccionNuevoAFOCLTablaValores = ({ formMethods, codSistemaPrincipal }: SeccionNuevoAFOCLTablaValoresProps) => {
  const { formState: { dirtyFields } } = formMethods;
  const bgColors = UseBgColor();

  const watchedUnidadNegocio = useWatch({ control: formMethods.control, name: OperacionAFOCLFields.CodUnidadNegocio });
  const watchedSectorComercial = useWatch({ control: formMethods.control, name: OperacionAFOCLFields.CodSectorComercial });
  const watchTablaValores = useWatch({ control: formMethods.control, name: OperacionAFOCLFields.TablaValores });

  const { headers, rows, aforos, isLoading } = useAFOCLTablaValorInicial(watchedUnidadNegocio, watchedSectorComercial);

  const aforoTipos = useAFOPUAforoTipos(codSistemaPrincipal);
  const loadAforoTipos = useLoadOptions({
    data: aforoTipos.aforoTipos,
    loading: aforoTipos.isLoading,
    isError: aforoTipos.isError,
    error: aforoTipos.error
  });

  useEffect(() => {
    if (aforos && aforos.length > 0) {

      const dataParaFormulario = aforos.map((item: OperacionAFOCLTablaValorVistaInicial) => {
        return {
          [OperacionAFOCLTablaValorFields.CodInstrumento]: item[OperacionAFOCLTablaValorFields.CodInstrumento],
          [OperacionAFOCLTablaValorFields.DescInstrumento]: item[OperacionAFOCLTablaValorFields.DescInstrumento],
          [OperacionAFOCLTablaValorFields.CodOperacionTipo]: item[OperacionAFOCLTablaValorFields.CodOperacionTipo],
          [OperacionAFOCLTablaValorFields.DescOperacionTipo]: item[OperacionAFOCLTablaValorFields.DescOperacionTipo],
          [OperacionAFOCLTablaValorFields.ValorCliente]: item[OperacionAFOCLTablaValorFields.ValorCliente],
          [OperacionAFOCLTablaValorFields.ValorPuente]: item[OperacionAFOCLTablaValorFields.ValorPuente],
          [OperacionAFOCLTablaValorFields.OrdenInstrumento]: item.ordenInstrumento ?? 0,
        };
      });

      formMethods.setValue(OperacionAFOCLFields.TablaValores, dataParaFormulario);
    }
  }, [aforos, formMethods]);

  // generamos columnas de forma dinamica segun el pivot de los aforos
  const { columns, columnGroups } = useMemo(() => {
    const fieldName = OperacionAFOCLFields.TablaValores;

    if (!headers || headers.length === 0) {
      return { columns: [], columnGroups: undefined };
    }

    // no usa el formulario para generar la tabla porque hay race conditions y queda vacio
    // usa directamente la data de aforos. COmo esta en el mismo orden queda igual que el form
    const indexMap = new Map<string, number>();
    if (aforos) {
      aforos.forEach((item: OperacionAFOCLTablaValorVistaInicial, idx: number) => {
        const key = `${item[OperacionAFOCLTablaValorFields.CodInstrumento]}-${item[OperacionAFOCLTablaValorFields.CodOperacionTipo]}`;
        indexMap.set(key, idx);
      });
    }

    const cols: ITableColumn[] = [
      {
        label: 'INSTRUMENTO',
        value: OperacionAFOCLTablaValorFields.DescInstrumento,
        onRenderCell: (row: AFOCLRow) => (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {row.descInstrumento}
            </Typography>
          </Box>
        ),
      }
    ];

    // Recorremos los headers pero los convertimos en COLUMNAS directas
    headers.forEach((header) => {
      cols.push({
        label: header.descOperacionTipo.toUpperCase(),
        onRenderCell: (row: AFOCLRow) => {
          const celda = row.celdas[header.codOperacionTipo];

          if (!celda) return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 1, minWidth: '160px' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Puente: <b>No Aplica</b></Typography>
              <AsyncSelect label="Cliente" disabled value="No Aplica" control={formMethods.control} name="" loadOptions={loadAforoTipos} />
            </Box>
          );

          const key = `${celda[OperacionAFOCLTablaValorFields.CodInstrumento]}-${celda[OperacionAFOCLTablaValorFields.CodOperacionTipo]}`;
          const itemIndex = indexMap.get(key);

          // Fallback de seguridad: si no hay index, no intentamos registrar un select con index 'undefined'
          if (itemIndex === undefined) return <></>;
          const isDirty = dirtyFields.tablaValores?.[itemIndex]?.[OperacionAFOCLTablaValorFields.ValorCliente];

          return (
            <Box sx={{
              display: 'flex', flexDirection: 'column', gap: 1, py: 1, minWidth: '160px',
              
            }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', mb: 1 }}>
                Aforo Puente: <b>{celda[OperacionAFOCLTablaValorFields.ValorPuente]}</b>
              </Typography>

              <AsyncSelect
                loadOptions={loadAforoTipos}
                label='Aforo Cliente'
                control={formMethods.control}
                name={`${fieldName}.${itemIndex}.${OperacionAFOCLTablaValorFields.ValorCliente}`}
                disabled={!celda[OperacionAFOCLTablaValorFields.PermiteEditar]}
                valueField={EntityWithIdAndDescriptionFields.Description}
                selectNotNulleable
                sx={{ width: '100%', backgroundColor: isDirty ? bgColors.primaryLight : 'transparent', }}
              />
            </Box>
          );
        }
      });
    });

    return { columns: cols, columnGroups: undefined };
  }, [headers, aforos, formMethods.control, loadAforoTipos]);

  return (
    <CardActions title='Tabla de Valores' icon='mdi:table-edit' colapsable tableContent>
      {!(watchedUnidadNegocio && watchedSectorComercial)
        ? (
          <Box sx={{ px: 4, py: 3 }}>
            <Alert severity={'info'}>
              Seleccione Unidad de Negocio y Sector comercial para cargar valores.
            </Alert>
          </Box>
        )
        : (
          <TableList
            entityList={rows}
            columns={columns}
            columnGroups={columnGroups}
            isLoading={isLoading}
            error={false}
          />
        )
      }
      {formMethods.formState.errors.tablaValores?.message && (
        <Box sx={{ px: 4 }}>
          <FormHelperText error>
            {formMethods.formState.errors.tablaValores.message}
          </FormHelperText>
        </Box>
      )}
    </CardActions>
  );
}

export default SeccionNuevoAFOCLTablaValores